"use client";

import { useEffect, useMemo, useState } from "react";
import { IoClose } from "react-icons/io5";
import teacherAPI from "../../../services/teacher/teacher";

type RankingRow = {
  name: string;
  createGameCount: number;
};

const TOP_LIST_LIMIT = 10;
const POPUP_PAGE_SIZE = 20;

function buildPaginationItems(currentPage: number, totalPages: number): Array<number | "ellipsis"> {
  if (totalPages <= 6) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, 4, "ellipsis", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, "ellipsis", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis", currentPage - 1, currentPage, currentPage + 1, "ellipsis", totalPages];
}

function normalizeRankingRows(response: unknown): RankingRow[] {
  const sourceRows = Array.isArray(response)
    ? response
    : Array.isArray((response as { data?: unknown[] })?.data)
      ? (response as { data: unknown[] }).data
      : [];

  return sourceRows
    .map((item) => {
      const row = item as { name?: unknown; create_game_count?: unknown; createGameCount?: unknown };
      const name = String(row.name ?? "").trim();
      const rawCount = row.create_game_count ?? row.createGameCount;
      const count = Number(rawCount);

      if (!name || !Number.isFinite(count)) {
        return null;
      }

      return {
        name,
        createGameCount: count,
      };
    })
    .filter(Boolean) as RankingRow[];
}

export default function UserEngagementRanking() {
  const [rows, setRows] = useState<RankingRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const loadRanking = async () => {
    setIsLoading(true);
    const response = await teacherAPI.teacherCountCreateGameRanking();

    if (!response || (response as { status?: string })?.status === "failed") {
      setRows([]);
      setIsLoading(false);
      return;
    }

    setRows(normalizeRankingRows(response));
    setIsLoading(false);
  };

  useEffect(() => {
    void loadRanking();
  }, []);

  useEffect(() => {
    if (!isPopupOpen) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isPopupOpen]);

  const topRows = useMemo(() => rows.slice(0, TOP_LIST_LIMIT), [rows]);
  const totalPages = Math.max(1, Math.ceil(rows.length / POPUP_PAGE_SIZE));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * POPUP_PAGE_SIZE;
    return rows.slice(start, start + POPUP_PAGE_SIZE);
  }, [rows, currentPage]);
  const paginationItems = useMemo(
    () => buildPaginationItems(currentPage, totalPages),
    [currentPage, totalPages],
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const renderTable = (tableRows: RankingRow[]) => {
    if (isLoading) {
      return <div className="px-4 py-6 text-sm text-[#7a7a7a]">กำลังโหลด...</div>;
    }

    if (tableRows.length === 0) {
      return <div className="px-4 py-6 text-sm text-[#7a7a7a]">ไม่พบข้อมูล</div>;
    }

    return (
      <div className="overflow-x-auto px-4 pb-3">
        <table className="min-w-full border-separate border-spacing-y-0">
          <thead>
            <tr className="text-left text-sm font-semibold text-[#5a5a5a]">
              <th className="w-14 px-4 py-2"></th>
              <th className="px-4 py-2"></th>
              <th className="w-28 px-4 py-2 text-right">หน่วย/เกม</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, index) => {
              const rowNumber = isPopupOpen
                ? (currentPage - 1) * POPUP_PAGE_SIZE + index + 1
                : index + 1;
              const isEven = index % 2 === 1;

              return (
                <tr
                  key={`${row.name}_${rowNumber}`}
                  className={isEven ? "bg-[#ECEFF6]" : "bg-[#F7F7F7]"}
                >
                  <td className="px-4 py-2 text-lg font-normal text-[#4E4E4E]">{rowNumber}</td>
                  <td className="px-4 py-2 text-lg font-normal text-[#4E4E4E]">{row.name}</td>
                  <td className="px-4 py-2 text-right text-lg font-normal text-[#4E4E4E]">{row.createGameCount}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <div>
        <div className="flex items-center justify-between px-4 pt-4">
          <h1 className="text-lg font-bold text-[#4E4E4E]">อันดับการมีส่วนร่วมของ User</h1>
          <button
            type="button"
            onClick={() => {
              setCurrentPage(1);
              setIsPopupOpen(true);
            }}
            className="text-base font-normal text-[#0092D7] hover:underline cursor-pointer"
          >
            ดูทั้งหมด
          </button>
        </div>

        {renderTable(topRows)}
      </div>

      {isPopupOpen ? (
        <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-[900px] rounded-2xl bg-white shadow-[0_12px_35px_rgba(0,0,0,0.35)]">
            <div className="flex items-center justify-between px-5 py-4">
              <h2 className="text-xl font-semibold text-[#4E4E4E]">อันดับการมีส่วนร่วมของ User</h2>
              <button
                type="button"
                onClick={() => setIsPopupOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#d7dade] text-[#4E4E4E] cursor-pointer transition hover:bg-[#c1c8d1]"
              >
                <IoClose size={22} />
              </button>
            </div>

            <div className="max-h-[80vh] overflow-y-auto pt-2">
              {renderTable(paginatedRows)}

              {!isLoading && rows.length > 0 ? (
                <div className="flex justify-center px-4 pb-5 pt-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      className="h-8 min-w-8 rounded border border-[#d9d9d9] px-2 text-sm text-[#4E4E4E] disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                    >
                      ก่อนหน้า
                    </button>

                    {paginationItems.map((item, index) => (
                      item === "ellipsis" ? (
                        <span key={`ellipsis_${index}`} className="px-1 text-[#8a8a8a]">...</span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setCurrentPage(item)}
                          className={`h-8 min-w-8 rounded border px-2 text-sm ${item === currentPage
                            ? "border-[#0092D7] bg-[#0092D7] text-white"
                            : "border-[#d9d9d9] text-[#4E4E4E]"} cursor-pointer`}
                        >
                          {item}
                        </button>
                      )
                    ))}

                    <button
                      type="button"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      className="h-8 min-w-8 rounded border border-[#d9d9d9] px-2 text-sm text-[#4E4E4E] disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                    >
                      ถัดไป
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}